/**
 * SKU 独立编辑页面（重构版）
 * 职责: 提供完整的SKU编辑界面，支持自动保存
 */

import React, { useEffect } from 'react';
import { Form, Card, Spin, Space, Tabs, Alert } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { SaveOutlined, LeftOutlined } from '@ant-design/icons';

// Components
import { PageHeader } from '../../components/Layout/PageHeader';
import { SKUFormBasicFields } from './SKUFormBasicFields';
import { SKUFormSupplyChainFields } from './SKUFormSupplyChainFields';
import { SKUFormInventoryFields } from './SKUFormInventoryFields';
import { SKUInventoryDetails } from './components/SKUInventoryDetails';
import { SKUFormLogisticsFields } from './SKUFormLogisticsFields';
import { SKUFormMediaFields } from './SKUFormMediaFields';
import { SaveStatusTag } from './components/SaveStatusTag';
import { SKUCertifications } from './components/SKUCertifications';

// Hooks
import { useSKUEditData } from './hooks/useSKUEditData';
import { useAutoSave } from './hooks/useAutoSave';


// Styles
import './SKUManagement.css';

export const SKUEditRefactored: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [form] = Form.useForm();

    // ========== 数据加载 ==========
    const {
        loading,
        sku,
        brands,
        brandMode,
        setBrandMode,
        loadSKU,
        loadBrands,
    } = useSKUEditData(id, form);

    // ========== 自动保存 ==========
    const {
        saveStatus,
        formChanged,
        handleFormChange,
        handleManualSave,
    } = useAutoSave(id, form);



    // ========== 初始化加载 ==========
    useEffect(() => {
        loadSKU();
        loadBrands();
    }, [loadSKU, loadBrands]);

    // ========== 返回列表 ==========
    const handleBack = () => {
        if (formChanged && saveStatus !== 'saved') {
            if (window.confirm('有未保存的更改，确定要离开吗？')) {
                navigate('/sku-management');
            }
        } else {
            navigate('/sku-management');
        }
    };

    // ========== 加载中状态 ==========
    if (loading) {
        return (
            <div className="loading-container">
                <Spin size="large" />
            </div>
        );
    }

    // ========== 主渲染 ==========
    return (
        <div className="sku-management-page">
            <PageHeader
                title={`编辑 SKU: ${sku?.name || ''}`}
                subtitle="修改后自动保存"
                breadcrumbs={[
                    { title: '首页', href: '/' },
                    { title: 'SKU管理', href: '/sku-management' },
                    { title: '编辑' },
                ]}
                actions={[
                    {
                        key: 'back',
                        label: '返回列表',
                        icon: <LeftOutlined />,
                        onClick: handleBack,
                    },
                    {
                        key: 'save',
                        label: '立即保存',
                        type: 'primary',
                        icon: <SaveOutlined />,
                        onClick: handleManualSave,
                    },
                ]}
                extra={
                    <Space>
                        <SaveStatusTag status={saveStatus} />
                    </Space>
                }
            />

            <div className="sku-management-content">
                <Card className="sku-edit-card" bordered={false}>
                    <Form.Item style={{ marginBottom: 16 }}>
                        <Alert
                            message="定价与成本分析已移至运营策略模块"
                            description={
                                <span>
                                    如需进行定价参数配置、成本计算和运营策略分析，请前往{' '}
                                    <a href="/operation-strategy" onClick={(e) => {
                                        e.preventDefault();
                                        navigate('/operation-strategy');
                                    }}>运营策略</a> 模块。
                                </span>
                            }
                            type="info"
                            showIcon
                            closable
                        />
                    </Form.Item>
                    <Form
                        form={form}
                        layout="vertical"
                        onValuesChange={handleFormChange}
                    >
                        <SKUFormBasicFields
                            brands={brands}
                            brandMode={brandMode}
                            onBrandModeChange={setBrandMode}
                        />

                        <SKUFormSupplyChainFields />

                        <SKUFormInventoryFields />
                        <SKUInventoryDetails
                            batches={sku?.inventory_batches}
                            summary={sku?.inventory_summary}
                        />

                        <SKUFormLogisticsFields />

                        <SKUFormMediaFields />

                        <div style={{ marginTop: 24 }}>
                            {sku?.id ? <SKUCertifications skuId={sku.id} /> : null}
                        </div>
                    </Form>
                </Card>
            </div>


        </div>
    );
};

// 导出重构版本
export { SKUEditRefactored as SKUEdit };
export default SKUEditRefactored;
